// JobLaborStrip — Phase 10 R2
//
// Per-row sell-side labor display + manual override popover.
// Rendered only when labor_engine_enabled && ui_ticket_financials are ON.
//
// SELL-SIDE ONLY. Never feed into pay (R3) or cost (R4) engines.

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DollarSign, Pencil, Loader2, RotateCcw } from 'lucide-react';
import type { TicketLaborLine } from '@/hooks/labor/useTicketLaborLines';
import { useUpdateJobLaborOverride } from '@/hooks/labor/useUpdateJobLaborOverride';
import { useComputeJobLabor } from '@/hooks/labor/useComputeJobLabor';
import { useUserRole } from '@/hooks/useUserRole';

const fmt = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

function sourceLabel(source: string | null, overridden: boolean): string {
  if (overridden) return 'Override';
  switch (source) {
    case 'diagnostic': return 'Diagnostic rate';
    case 'maintenance': return 'Maintenance rate';
    case 'default': return 'Default rate';
    case 'override': return 'Override';
    default: return 'Labor';
  }
}

interface Props {
  ticketId: string;
  line: TicketLaborLine;
}

export function JobLaborStrip({ ticketId, line }: Props) {
  const { role } = useUserRole();
  const canEditRate = role === 'OWNER' || role === 'MANAGER' || role === 'SERVICE_WRITER';

  const overrideMut = useUpdateJobLaborOverride();
  const computeMut = useComputeJobLabor();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draftRate, setDraftRate] = useState<string>(
    line.labor_rate_applied != null ? String(line.labor_rate_applied) : '',
  );

  // ── Uncomputed legacy row → "Compute labor" affordance only ───────────
  if (line.is_uncomputed) {
    return (
      <div className="mt-2 flex items-center justify-between rounded-md border border-dashed bg-muted/30 px-2.5 py-1.5 text-xs">
        <span className="text-muted-foreground">Labor not computed</span>
        {canEditRate && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            disabled={computeMut.isPending}
            onClick={() =>
              computeMut.mutate({ ticketId, jobId: line.job_id })
            }
          >
            {computeMut.isPending ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <DollarSign className="h-3 w-3 mr-1" />
            )}
            Compute labor
          </Button>
        )}
      </div>
    );
  }

  const sold_hours = (line.sold_minutes ?? 0) / 60;
  const rate = line.labor_rate_applied ?? 0;
  const sell = line.labor_sell_amount ?? 0;
  const reasonText = line.labor_rate_reason ?? '';

  const handleApplyOverride = () => {
    const parsed = parseFloat(draftRate);
    if (isNaN(parsed) || parsed <= 0) return;
    overrideMut.mutate(
      { jobId: line.job_id, ticketId, overrideRate: parsed },
      {
        onSuccess: (res) => {
          if (!(res as any)?.skipped) setPopoverOpen(false);
        },
      },
    );
  };

  const handleRevert = () => {
    overrideMut.mutate(
      { jobId: line.job_id, ticketId, overrideRate: null },
      {
        onSuccess: (res) => {
          if (!(res as any)?.skipped) setPopoverOpen(false);
        },
      },
    );
  };

  return (
    <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-background/60 px-2.5 py-1.5 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-foreground truncate">
          <span className="tabular-nums">{sold_hours.toFixed(2)} hrs</span>
          <span className="text-muted-foreground"> × </span>
          <span className="tabular-nums">{fmt(rate)}/hr</span>
          <span className="text-muted-foreground"> = </span>
          <span className="font-medium tabular-nums">{fmt(sell)}</span>
        </span>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant={line.labor_rate_overridden ? 'default' : 'secondary'}
                className="text-[10px] h-5 px-1.5"
              >
                {sourceLabel(line.labor_rate_source, line.labor_rate_overridden)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              <div className="space-y-1">
                <div>{reasonText}</div>
                {line.labor_rate_overridden && line.labor_rate_resolved != null && (
                  <div className="text-muted-foreground">
                    Resolved rate was {fmt(line.labor_rate_resolved)}/hr
                  </div>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {canEditRate && (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-5 w-5">
                <Pencil className="h-3 w-3" />
                <span className="sr-only">Override labor rate</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" align="end" className="w-64 p-3">
              <div className="space-y-3">
                <div>
                  <Label htmlFor="labor-override-rate" className="text-xs">
                    Override labor rate ($/hr)
                  </Label>
                  <Input
                    id="labor-override-rate"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={draftRate}
                    onChange={(e) => setDraftRate(e.target.value)}
                    className="mt-1 h-8"
                  />
                  {line.labor_rate_resolved != null && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Resolved: {fmt(line.labor_rate_resolved)}/hr
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  {line.labor_rate_overridden && line.labor_rate_resolved != null && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={overrideMut.isPending}
                      onClick={handleRevert}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Revert
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-3 text-xs ml-auto"
                    disabled={overrideMut.isPending}
                    onClick={handleApplyOverride}
                  >
                    {overrideMut.isPending && (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    )}
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
